import { useState } from 'react';
import { reorderQuestionsByDrag } from '../utils/questionOrdering';

/**
 * Manages all drag-and-drop systems: questions, default questions, options, and page cards.
 */
export function useEditorDragDrop({
  questions,
  activePageQuestions,
  activePage,
  defaultQuestions,
  questionPages,
  applyEditorStructure,
  normalizeQuestionConditionals,
  setDefaultQuestions,
  updateQuestion,
  updateDefaultQuestion,
  buildOrderedQuestionFlowIds,
  applyOrderedQuestionFlowIds,
  setSelectedPageId
}) {
  // Regular question drag state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Option drag state (shared between regular and default)
  const [draggedOptionIndex, setDraggedOptionIndex] = useState(null);
  const [dragOverOptionIndex, setDragOverOptionIndex] = useState(null);

  // Default question drag state
  const [defaultDraggedIndex, setDefaultDraggedIndex] = useState(null);
  const [defaultDragOverIndex, setDefaultDragOverIndex] = useState(null);

  // Page card drag state
  const [draggedPageCardId, setDraggedPageCardId] = useState(null);
  const [dragOverPageCardId, setDragOverPageCardId] = useState(null);

  // --- Regular question drag handlers ---
  const handleDragStart = (e, index) => {
    const target = e.target;

    const isInteractiveElement = (element) => {
      if (!element) return false;
      if (element.tagName === 'INPUT' ||
        element.tagName === 'TEXTAREA' ||
        element.tagName === 'SELECT' ||
        element.tagName === 'BUTTON' ||
        element.contentEditable === 'true' ||
        element.closest('input') ||
        element.closest('textarea') ||
        element.closest('select') ||
        element.closest('button') ||
        element.closest('[contenteditable="true"]') ||
        element.closest('[role="button"]') ||
        element.closest('[data-interactive]') ||
        element.closest('.formatting-toolbar') ||
        element.closest('.question-options') ||
        element.closest('.file-input-container') ||
        element.closest('.content-editable-field')) {
        return true;
      }
      if (element.classList.contains('format-button') ||
        element.classList.contains('option-input') ||
        element.classList.contains('file-input') ||
        element.classList.contains('dropdown-select') ||
        element.classList.contains('content-editable-field')) {
        return true;
      }
      if (element.closest('.drag-handle') || element.closest('.cursor-grab') || element.closest('.cursor-grabbing')) {
        return false;
      }
      return false;
    };

    let currentElement = target;
    while (currentElement && currentElement !== document.body) {
      if (isInteractiveElement(currentElement)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      currentElement = currentElement.parentElement;
    }

    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    if (!activePage) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const pageQuestions = reorderQuestionsByDrag(activePageQuestions, draggedIndex, dropIndex);
    if (pageQuestions === activePageQuestions) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const pageQuestionIds = new Set(activePageQuestions.map((question) => question.id));
    let pageCursor = 0;
    const reorderedQuestions = questions.map((question) => {
      if (!pageQuestionIds.has(question.id)) return question;
      const nextQuestion = pageQuestions[pageCursor];
      pageCursor += 1;
      return nextQuestion;
    });

    applyEditorStructure(normalizeQuestionConditionals(reorderedQuestions), questionPages);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // --- Default question drag handlers ---
  const handleDefaultDragStart = (e, index) => {
    const target = e.target;

    const isInteractiveElement = (element) => {
      if (!element) return false;
      if (element.tagName === 'INPUT' ||
        element.tagName === 'TEXTAREA' ||
        element.tagName === 'SELECT' ||
        element.tagName === 'BUTTON' ||
        element.contentEditable === 'true' ||
        element.closest('input') ||
        element.closest('textarea') ||
        element.closest('select') ||
        element.closest('button') ||
        element.closest('[contenteditable="true"]') ||
        element.closest('[role="button"]') ||
        element.closest('[data-interactive]') ||
        element.closest('.formatting-toolbar') ||
        element.closest('.question-options') ||
        element.closest('.file-input-container') ||
        element.closest('.content-editable-field')) {
        return true;
      }
      if (element.classList.contains('format-button') ||
        element.classList.contains('option-input') ||
        element.classList.contains('file-input') ||
        element.classList.contains('dropdown-select') ||
        element.classList.contains('content-editable-field')) {
        return true;
      }
      if (element.closest('.drag-handle') || element.closest('.cursor-grab') || element.closest('.cursor-grabbing')) {
        return false;
      }
      return false;
    };

    let currentElement = target;
    while (currentElement && currentElement !== document.body) {
      if (isInteractiveElement(currentElement)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      currentElement = currentElement.parentElement;
    }

    setDefaultDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDefaultDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDefaultDragOverIndex(index);
  };

  const handleDefaultDragLeave = (e) => {
    setDefaultDragOverIndex(null);
  };

  const handleDefaultDrop = (e, dropIndex) => {
    e.preventDefault();
    if (defaultDraggedIndex === null || defaultDraggedIndex === dropIndex) {
      setDefaultDraggedIndex(null);
      setDefaultDragOverIndex(null);
      return;
    }

    const newQuestions = reorderQuestionsByDrag(defaultQuestions, defaultDraggedIndex, dropIndex);
    if (newQuestions === defaultQuestions) {
      setDefaultDraggedIndex(null);
      setDefaultDragOverIndex(null);
      return;
    }

    setDefaultQuestions(normalizeQuestionConditionals(newQuestions));
    setDefaultDraggedIndex(null);
    setDefaultDragOverIndex(null);
  };

  const handleDefaultDragEnd = () => {
    setDefaultDraggedIndex(null);
    setDefaultDragOverIndex(null);
  };

  // --- Option drag handlers (regular questions) ---
  const handleOptionDragStart = (qId, optIndex) => {
    setDraggedOptionIndex(optIndex);
    setDraggedIndex(null);
  };

  const handleOptionDragOver = (e, qId, optIndex) => {
    e.preventDefault();
    setDragOverOptionIndex(optIndex);
  };

  const handleOptionDragEnd = (qId) => {
    if (draggedOptionIndex !== null && dragOverOptionIndex !== null && draggedOptionIndex !== dragOverOptionIndex) {
      const question = questions.find(q => q.id === qId);
      if (question && question.options) {
        const newOptions = [...question.options];
        const draggedOption = newOptions[draggedOptionIndex];
        newOptions.splice(draggedOptionIndex, 1);
        newOptions.splice(dragOverOptionIndex, 0, draggedOption);
        updateQuestion(qId, { options: newOptions });
      }
    }
    setDraggedOptionIndex(null);
    setDragOverOptionIndex(null);
  };

  // --- Option drag handlers (default questions) ---
  const handleDefaultOptionDragStart = (qId, optIndex) => {
    setDraggedOptionIndex(optIndex);
    setDefaultDraggedIndex(null);
  };

  const handleDefaultOptionDragOver = (e, qId, optIndex) => {
    e.preventDefault();
    setDragOverOptionIndex(optIndex);
  };

  const handleDefaultOptionDragEnd = (qId) => {
    if (draggedOptionIndex !== null && dragOverOptionIndex !== null && draggedOptionIndex !== dragOverOptionIndex) {
      const question = defaultQuestions.find(q => q.id === qId);
      if (question && question.options) {
        const newOptions = [...question.options];
        const draggedOption = newOptions[draggedOptionIndex];
        newOptions.splice(draggedOptionIndex, 1);
        newOptions.splice(dragOverOptionIndex, 0, draggedOption);
        updateDefaultQuestion(qId, { options: newOptions });
      }
    }
    setDraggedOptionIndex(null);
    setDragOverOptionIndex(null);
  };

  // --- Page card drag handlers ---
  const isPageCardInteractionTarget = (target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest('input, textarea, select, button, a, [contenteditable="true"], [role="button"], [data-interactive]')
    );
  };

  const handlePageCardDragStart = (event, pageId) => {
    if (isPageCardInteractionTarget(event.target)) {
      event.preventDefault();
      return;
    }
    setDraggedPageCardId(pageId);
    setDragOverPageCardId(null);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', pageId);
  };

  const handlePageCardDragOver = (event, pageId) => {
    if (!draggedPageCardId || draggedPageCardId === pageId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragOverPageCardId !== pageId) {
      setDragOverPageCardId(pageId);
    }
  };

  const handlePageCardDragLeave = (pageId) => {
    if (dragOverPageCardId === pageId) {
      setDragOverPageCardId(null);
    }
  };

  const handlePageCardDrop = (event, targetPageId) => {
    event.preventDefault();
    if (!draggedPageCardId || draggedPageCardId === targetPageId) {
      setDraggedPageCardId(null);
      setDragOverPageCardId(null);
      return;
    }

    const orderedIds = buildOrderedQuestionFlowIds();
    const fromIndex = orderedIds.findIndex((id) => id === draggedPageCardId);
    const toIndex = orderedIds.findIndex((id) => id === targetPageId);

    if (fromIndex === -1 || toIndex === -1) {
      setDraggedPageCardId(null);
      setDragOverPageCardId(null);
      return;
    }

    const reorderedIds = [...orderedIds];
    const [movedId] = reorderedIds.splice(fromIndex, 1);
    reorderedIds.splice(toIndex, 0, movedId);
    applyOrderedQuestionFlowIds(reorderedIds);

    setDraggedPageCardId(null);
    setDragOverPageCardId(null);
  };

  const handlePageCardDragEnd = () => {
    setDraggedPageCardId(null);
    setDragOverPageCardId(null);
  };

  const handlePageCardSelect = (event, pageId) => {
    if (isPageCardInteractionTarget(event.target)) return;
    setSelectedPageId(pageId);
  };

  return {
    // State + setters (setters needed by QuestionEditor onClick handlers)
    draggedIndex, setDraggedIndex, dragOverIndex, setDragOverIndex,
    draggedOptionIndex, dragOverOptionIndex,
    defaultDraggedIndex, setDefaultDraggedIndex, defaultDragOverIndex, setDefaultDragOverIndex,
    draggedPageCardId, dragOverPageCardId,
    // Regular question drag handlers
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
    // Default question drag handlers
    handleDefaultDragStart, handleDefaultDragOver, handleDefaultDragLeave, handleDefaultDrop, handleDefaultDragEnd,
    // Option drag handlers (regular)
    handleOptionDragStart, handleOptionDragOver, handleOptionDragEnd,
    // Option drag handlers (default)
    handleDefaultOptionDragStart, handleDefaultOptionDragOver, handleDefaultOptionDragEnd,
    // Page card drag handlers
    handlePageCardDragStart, handlePageCardDragOver, handlePageCardDragLeave,
    handlePageCardDrop, handlePageCardDragEnd, handlePageCardSelect
  };
}
